<template>
  <md-dialog class="moduleSettingsDialog" :md-active.sync="localActive">
    <md-dialog-title class="ms-title">
      {{ (options.Lang && options.Lang.module_settings_title) || 'Checkout Settings' }}
    </md-dialog-title>

    <div class="ms-body">
      <p class="ms-intro">
        {{ (options.Lang && options.Lang.module_settings_intro)
          || 'Activate the structural changes for the checkout flow. The wording for each section is edited per language under Branding.' }}
      </p>

      <div v-for="opt in activeSettingDefs" :key="opt.key" class="ms-row">
        <div class="ms-row-text">
          <p class="ms-row-name">{{ labelFor(opt) }}</p>
          <p class="ms-row-desc">{{ descFor(opt) }}</p>
        </div>
        <md-switch
            v-model="draft[opt.key]"
            class="md-primary ms-switch"
        />
      </div>
    </div>

    <md-dialog-actions class="ms-actions">
      <md-button class="md-raised secondary" @click="close">
        <strong>{{ (options.Lang && options.Lang.close_window) || 'Close' }}</strong>
      </md-button>
      <md-button class="md-raised md-primary" :disabled="saving" @click="save">
        <strong>{{ (options.Lang && options.Lang.save) || 'Save' }}</strong>
      </md-button>
    </md-dialog-actions>
  </md-dialog>
</template>

<script>
// Per-module structural toggles. Each module shows only its own settings —
// modules are independent (the Luggage block has nothing to do with the
// checkout module). label/desc resolve from LangFile with English fallbacks
// so the panel works before translation.
//
// This is the single source of truth for "which modules have an editable
// settings panel": the gear icon in Modules.vue keys off the same map, so a
// module with no entry here gets a disabled gear automatically.
export const SETTING_DEFS_BY_MODULE = {
  // The former invoice_checkout structural toggles (insurance confirm, name
  // disclaimer, custom terms, payment confirm, payment schedule) now live as
  // per-profile box-visibility toggles on the Checkout Profile, so they are no
  // longer offered here.
  luggage_show: [
    { key: 'show_weight', labelKey: 'module_setting_luggage_weight_label', descKey: 'module_setting_luggage_weight_desc',
      labelDef: 'Show luggage weight', descDef: 'Show the baggage weight (e.g. 20kg) inside the luggage block. When off, the block still shows but without weights.' }
  ]
};

export default {
  name: 'ModuleSettingsDialog',
  props: {
    options: { type: Object, required: true },
    // Two-way visibility, mirrors the CreateTabModal `show.sync` pattern.
    show: { type: Boolean, default: false },
    // The module whose settings we're editing (full row incl. `settings`).
    module: { type: Object, default: null }
  },
  data() {
    return {
      saving: false,
      // Local editable copy of the toggles; seeded from the module on open.
      draft: {},
      settingDefsByModule: SETTING_DEFS_BY_MODULE
    };
  },
  computed: {
    localActive: {
      get() { return this.show; },
      set(v) { if (!v) this.$emit('update:show', false); }
    },
    // The toggles for the currently-opened module (empty if it has none).
    activeSettingDefs() {
      const name = this.module && this.module.name;
      return (name && this.settingDefsByModule[name]) || [];
    }
  },
  watch: {
    show(open) {
      if (open) this.seedDraft();
    }
  },
  methods: {
    labelFor(opt) {
      return (this.options.Lang && this.options.Lang[opt.labelKey]) || opt.labelDef;
    },
    descFor(opt) {
      return (this.options.Lang && this.options.Lang[opt.descKey]) || opt.descDef;
    },
    seedDraft() {
      const current = (this.module && this.module.settings) || {};
      const draft = {};
      this.activeSettingDefs.forEach(opt => {
        // settings may arrive as a JSON string (raw row) or parsed object.
        const parsed = typeof current === 'string' ? this.safeParse(current) : current;
        // Values may be real booleans, numbers (1/0) or strings ("1"/"0").
        // Coerce explicitly — note !!"0" is true, so never rely on truthiness.
        const v = parsed && parsed[opt.key];
        draft[opt.key] = (v === true || v === 1 || v === '1');
      });
      this.draft = draft;
    },
    safeParse(str) {
      try { return JSON.parse(str); } catch (e) { return {}; }
    },
    close() {
      this.$emit('update:show', false);
    },
    async save() {
      const self = this;
      this.saving = true;
      const settings = {};
      this.activeSettingDefs.forEach(opt => { settings[opt.key] = this.draft[opt.key] ? 1 : 0; });
      const payload = { data: { moduleName: this.module && this.module.name, settings } };
      await this.doPost(payload, 'updateModulSettings', function (res) {
        self.saving = false;
        if (res.data.status === 'error') {
          self.help.log(self.$t(res.msg), false, true);
        } else {
          self.$emit('saved', settings);
          self.$emit('update:show', false);
        }
      });
    }
  }
};
</script>

<style scoped>
/* Vue Material renders the dialog inside .md-dialog / .md-dialog-container
   which carry their own (zero) padding at high specificity. Scoped styles get
   out-specified, so the inner spacing needs !important and the container needs
   ::v-deep to be reached at all. */
.moduleSettingsDialog {
  max-width: 640px !important;
  width: 640px !important;
  margin: auto !important;
  border-radius: 12px !important;
}
.moduleSettingsDialog ::v-deep .md-dialog-container {
  padding: 0 !important;
  border-radius: 12px !important;
  overflow: hidden !important;
}
.ms-title {
  font-size: 1.6rem !important;
  padding: 1.5rem 2rem 0.5rem !important;
  margin: 0 !important;
}
.ms-body {
  padding: 0 2rem 1rem !important;
}
.ms-intro {
  font-size: 0.95rem !important;
  color: #555 !important;
  margin: 0 0 1.2rem !important;
}
.ms-row {
  display: flex !important;
  align-items: flex-start !important;
  justify-content: space-between !important;
  gap: 1.5rem !important;
  padding: 0.9rem 0 !important;
  border-bottom: 1px solid #eee !important;
}
.ms-row:last-child {
  border-bottom: none !important;
}
.ms-row-text {
  flex: 1 !important;
}
.ms-row-name {
  font-weight: 600 !important;
  font-size: 1rem !important;
  margin: 0 0 0.25rem !important;
}
.ms-row-desc {
  font-size: 0.85rem !important;
  color: #777 !important;
  margin: 0 !important;
  line-height: 1.35 !important;
}
.ms-switch {
  margin-top: 0.2rem !important;
  flex-shrink: 0 !important;
}
.ms-actions {
  padding: 0.5rem 2rem 1.5rem !important;
  margin: 0 !important;
}
</style>
